import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const UsageDashboard = () => {
  const navigate = useNavigate();

  const [usageData, setUsageData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState('');

  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });

  const [quickDateOption, setQuickDateOption] = useState('mtd');

  useEffect(() => {
    document.title = 'Multi-Tenant Usage Dashboard';
    fetchUsageData();
  }, []);

  const fetchUsageData = async () => {
    // Use the current dateRange state
    const { startDate, endDate } = dateRange;
    fetchDataWithDates(startDate, endDate);
  };

  // New function to fetch data with specific dates
  const fetchDataWithDates = async (startDate, endDate) => {
    try {
      setLoading(true);

      // Format dates exactly like the original HTML implementation
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];

      // Update the state for UI display
      setDateRange({ startDate, endDate });

      // Use fetch instead of axios to match the original HTML implementation
      const url = `/api/billing/usage?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
      console.log(`Fetching data with date range: ${formattedStartDate} to ${formattedEndDate}`);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Usage data:', data);

      // Check if any tenant data has date range info
      if (Object.keys(data).length > 0) {
        const firstTenant = Object.keys(data)[0];
        if (data[firstTenant] && data[firstTenant].startDate && data[firstTenant].endDate) {
          console.log(`Server returned date range: ${data[firstTenant].startDate} to ${data[firstTenant].endDate}`);
        }
      }

      setUsageData(data);

      // Set the first tenant as selected if there are tenants and none is selected
      if (Object.keys(data).length > 0 && !selectedTenant) {
        setSelectedTenant(Object.keys(data)[0]);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setError('Failed to load usage data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickDateChange = (e) => {
    const option = e.target.value;
    setQuickDateOption(option);

    const today = new Date();

    let start, end;

    switch (option) {
      case 'today':
        // For "Today", use the exact same date for both start and end
        start = new Date(today);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        start = new Date(today);
        start.setDate(today.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setDate(today.getDate() - 1);
        end.setHours(0, 0, 0, 0);
        break;
      case 'last7days':
        end = new Date(today);
        end.setHours(0, 0, 0, 0);
        start = new Date(today);
        start.setDate(today.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        break;
      case 'mtd':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(0, 0, 0, 0);
        break;
      case 'lastmonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        end.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        return; // Don't change dates for custom option
      default:
        return;
    }

    console.log(`Setting date range for ${option}:`, {
      start: format(start, 'yyyy-MM-dd HH:mm:ss'),
      end: format(end, 'yyyy-MM-dd HH:mm:ss')
    });

    // For non-custom options, fetch data with the new date range directly
    if (option !== 'custom') {
      fetchDataWithDates(start, end);
    } else {
      setDateRange({ startDate: start, endDate: end });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Prepare data for token usage chart
  const prepareTokenChartData = () => {
    return Object.entries(usageData).map(([tenant, data]) => ({
      tenant,
      inputTokens: data.inputTokens || 0,
      outputTokens: data.outputTokens || 0,
      totalTokens: data.totalTokens || 0
    }));
  };

  // Prepare data for cost distribution chart
  const prepareCostChartData = () => {
    return Object.entries(usageData).map(([tenant, data]) => ({
      tenant,
      tokenCost: data.tokenCost || 0,
      storageCost: data.storageCost || 0,
      totalCost: data.totalCost || 0
    }));
  };

  // Prepare model breakdown data for the selected tenant
  const prepareModelChartData = () => {
    if (!selectedTenant || !usageData[selectedTenant] || !usageData[selectedTenant].modelBreakdown) {
      return [];
    }

    return usageData[selectedTenant].modelBreakdown.map(model => ({
      modelName: model.modelName,
      tokens: model.totalTokens,
      cost: model.cost
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Typography variant="h4" color="error" align="center">
          {error}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="contained" onClick={fetchUsageData}>
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  const tokenChartData = prepareTokenChartData();
  const costChartData = prepareCostChartData();
  const modelChartData = prepareModelChartData();

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Multi-Tenant Usage Dashboard
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Date Range: {format(dateRange.startDate, 'MMM d, yyyy')} - {format(dateRange.endDate, 'MMM d, yyyy')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={quickDateOption}
              onChange={handleQuickDateChange}
              label="Date Range"
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="last7days">Last 7 Days</MenuItem>
              <MenuItem value="mtd">Month to Date</MenuItem>
              <MenuItem value="lastmonth">Last Month</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={dateRange.startDate}
              onChange={(newDate) => setDateRange(prev => ({ ...prev, startDate: newDate }))}
              disabled={quickDateOption !== 'custom'}
              slotProps={{ textField: { size: 'small' } }}
            />

            <DatePicker
              label="End Date"
              value={dateRange.endDate}
              onChange={(newDate) => setDateRange(prev => ({ ...prev, endDate: newDate }))}
              disabled={quickDateOption !== 'custom'}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>

          <Button
            variant="contained"
            onClick={fetchUsageData}
            disabled={quickDateOption !== 'custom'}
          >
            Apply
          </Button>
        </Box>
      </Box>

      {/* Tenant Usage Summary Table */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Tenant Usage Summary" />
        <CardContent>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="tenant usage table">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell align="right">Input Tokens</TableCell>
                  <TableCell align="right">Output Tokens</TableCell>
                  <TableCell align="right">Total Tokens</TableCell>
                  <TableCell align="right">Token Cost</TableCell>
                  <TableCell>Storage</TableCell>
                  <TableCell align="right">Storage Cost</TableCell>
                  <TableCell align="right">Chats</TableCell>
                  <TableCell align="right">Messages</TableCell>
                  <TableCell align="right">Avg Duration</TableCell>
                  <TableCell align="right">Total Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(usageData).length > 0 ? (
                  Object.entries(usageData).map(([tenant, data]) => (
                    <TableRow key={tenant}>
                      <TableCell component="th" scope="row">
                        <Button
                          color="primary"
                          onClick={() => navigate(`/tenant-dashboard/${tenant}`)}
                        >
                          {tenant}
                        </Button>
                      </TableCell>
                      <TableCell align="right">{formatNumber(data.inputTokens || 0)}</TableCell>
                      <TableCell align="right">{formatNumber(data.outputTokens || 0)}</TableCell>
                      <TableCell align="right">{formatNumber(data.totalTokens || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(data.tokenCost || 0)}</TableCell>
                      <TableCell>{data.storageHuman || '0 bytes'}</TableCell>
                      <TableCell align="right">{formatCurrency(data.storageCost || 0)}</TableCell>
                      <TableCell align="right">{formatNumber(data.totalSessions || 0)}</TableCell>
                      <TableCell align="right">{formatNumber(data.totalMessages || 0)}</TableCell>
                      <TableCell align="right">{(data.avgSessionDuration || 0).toFixed(1)}</TableCell>
                      <TableCell align="right">{formatCurrency(data.totalCost || 0)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No usage data available. Try making some API calls first.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Token Usage by Tenant" />
            <CardContent sx={{ height: 400 }}>
              {tokenChartData.length > 0 ? (
                <BarChart
                  dataset={tokenChartData}
                  xAxis={[{ scaleType: 'band', dataKey: 'tenant' }]}
                  series={[
                    { dataKey: 'inputTokens', label: 'Input Tokens', valueFormatter: (value) => formatNumber(value) },
                    { dataKey: 'outputTokens', label: 'Output Tokens', valueFormatter: (value) => formatNumber(value) }
                  ]}
                  height={350}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary">
                    No token usage data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Cost Distribution" />
            <CardContent sx={{ height: 400 }}>
              {costChartData.length > 0 ? (
                <BarChart
                  dataset={costChartData}
                  xAxis={[{ scaleType: 'band', dataKey: 'tenant' }]}
                  series={[
                    { dataKey: 'tokenCost', label: 'Token Cost', valueFormatter: (value) => formatCurrency(value) },
                    { dataKey: 'storageCost', label: 'Storage Cost', valueFormatter: (value) => formatCurrency(value) }
                  ]}
                  height={350}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary">
                    No cost data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Model Usage Breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Model Usage Breakdown"
          action={
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Tenant</InputLabel>
              <Select
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                label="Tenant"
                disabled={Object.keys(usageData).length === 0}
              >
                {Object.keys(usageData).map(tenant => (
                  <MenuItem key={tenant} value={tenant}>{tenant}</MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        />
        <CardContent sx={{ height: 400 }}>
          {modelChartData.length > 0 ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <BarChart
                  dataset={modelChartData}
                  xAxis={[{ scaleType: 'band', dataKey: 'modelName' }]}
                  series={[
                    { dataKey: 'tokens', label: 'Tokens', valueFormatter: (value) => formatNumber(value) },
                  ]}
                  height={350}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PieChart
                  series={[
                    {
                      data: modelChartData.map(model => ({
                        id: model.modelName,
                        value: model.cost,
                        label: model.modelName
                      })),
                      valueFormatter: (value) => formatCurrency(value),
                    }
                  ]}
                  height={350}
                />
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="body1" color="text.secondary">
                No model data available for this tenant
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader title="Actions" />
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchUsageData}
            >
              Refresh Data
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UsageDashboard;